import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Zap, Database, Key, Shield, List, ArrowRight, ChevronDown, ChevronUp,
  HelpCircle, Settings, AlertCircle, CheckCircle, Play
} from 'lucide-react';
import { useTour } from '../tour/useTour';

interface SectionProps {
  icon: React.ReactNode;
  title: string;
  color: string;
  children: React.ReactNode;
}

function Section({ icon, title, color, children }: SectionProps) {
  const [open, setOpen] = useState(true);
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-6 py-4 hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <span className={`p-2 rounded-lg ${color}`}>{icon}</span>
          <h2 className="text-base font-semibold text-gray-900">{title}</h2>
        </div>
        {open ? <ChevronUp size={18} className="text-gray-400" /> : <ChevronDown size={18} className="text-gray-400" />}
      </button>
      {open && <div className="px-6 pb-6 pt-2">{children}</div>}
    </div>
  );
}

function Tag({ children, color = 'bg-indigo-100 text-indigo-700' }: { children: React.ReactNode; color?: string }) {
  return <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${color}`}>{children}</span>;
}

function InfoBox({ children, type = 'info' }: { children: React.ReactNode; type?: 'info' | 'warn' | 'tip' }) {
  const styles = {
    info: 'bg-blue-50 border-blue-200 text-blue-800',
    warn: 'bg-amber-50 border-amber-200 text-amber-800',
    tip: 'bg-green-50 border-green-200 text-green-800',
  };
  const icons = {
    info: <AlertCircle size={14} className="flex-shrink-0 mt-0.5" />,
    warn: <AlertCircle size={14} className="flex-shrink-0 mt-0.5" />,
    tip: <CheckCircle size={14} className="flex-shrink-0 mt-0.5" />,
  };
  return (
    <div className={`flex gap-2 text-xs border rounded-md px-3 py-2 mt-3 ${styles[type]}`}>
      {icons[type]}
      <span>{children}</span>
    </div>
  );
}

export default function Guide() {
  const subscriptionsEnabled = process.env.REACT_APP_ENABLE_SUBSCRIPTIONS !== 'false';
  const { startTour } = useTour({ subscriptionsEnabled, autoStart: false });

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      {/* Header */}
      <div>
        <div className="flex items-center gap-2 mb-1">
          <HelpCircle size={22} className="text-indigo-500" />
          <h1 className="text-2xl font-bold text-gray-900">How It All Works</h1>
        </div>
        <p className="text-sm text-gray-500">
          A guide to the key concepts — Task Determination, Models, Lists, Categories, and how API key settings tie them together.
        </p>
        <button
          onClick={startTour}
          className="inline-flex items-center gap-2 mt-3 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-lg transition-colors"
        >
          <Play size={14} />
          Take the tour
        </button>
      </div>

      {/* Flow overview */}
      <div className="bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-100 rounded-xl px-6 py-5">
        <h2 className="text-sm font-semibold text-gray-700 mb-4">Request flow — what happens when an API call arrives</h2>
        <div className="flex flex-wrap items-center gap-2 text-xs font-medium">
          {[
            { label: 'API Key', sub: 'authenticated', color: 'bg-indigo-100 text-indigo-700 border-indigo-200' },
            { label: 'Task Determination', sub: 'query classified', color: 'bg-purple-100 text-purple-700 border-purple-200' },
            { label: 'Model Pool', sub: 'filtered by key settings', color: 'bg-blue-100 text-blue-700 border-blue-200' },
            { label: 'Category Tags', sub: 'further narrows pool', color: 'bg-teal-100 text-teal-700 border-teal-200' },
            { label: 'Best Model', sub: 'picked by priority', color: 'bg-green-100 text-green-700 border-green-200' },
            { label: 'Response', sub: 'streamed back', color: 'bg-gray-100 text-gray-700 border-gray-200' },
          ].map((step, i, arr) => (
            <React.Fragment key={step.label}>
              <div className={`border rounded-lg px-3 py-2 text-center min-w-[100px] ${step.color}`}>
                <div>{step.label}</div>
                <div className="opacity-60 font-normal mt-0.5">{step.sub}</div>
              </div>
              {i < arr.length - 1 && <ArrowRight size={14} className="text-gray-400 flex-shrink-0" />}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Task Determination */}
      <Section
        icon={<Zap size={16} className="text-purple-600" />}
        title="Task Determination (TD)"
        color="bg-purple-100"
      >
        <p className="text-sm text-gray-600 mb-3">
          Before routing a query to an AI model, the system runs a lightweight analysis step called <strong>Task Determination</strong>.
          A dedicated TD model reads the query and returns a structured classification:
        </p>
        <div className="grid grid-cols-2 gap-3 mb-3 text-xs">
          {[
            { field: 'taskType', desc: 'e.g. coding, analysis, creative, math' },
            { field: 'complexity', desc: '1–10 score' },
            { field: 'requiresPrivacy', desc: 'true / false — triggers privacy-only model pool' },
            { field: 'requestType', desc: 'streaming or sync hint' },
            { field: 'customTags', desc: 'your own boolean tags (e.g. marketing: true)' },
          ].map(({ field, desc }) => (
            <div key={field} className="bg-gray-50 rounded-md px-3 py-2 border border-gray-100">
              <code className="text-indigo-600 font-mono">{field}</code>
              <p className="text-gray-500 mt-0.5">{desc}</p>
            </div>
          ))}
        </div>
        <p className="text-sm text-gray-600 mb-1">
          The TD model you configure under <Link to="/models" className="text-indigo-600 hover:underline">Models</Link> drives
          this step. Mark a model as <Tag>Task Determination</Tag> and <Tag color="bg-green-100 text-green-700">Privacy Compliant</Tag> — the
          highest-ranked one matching both criteria is used.
        </p>
        <InfoBox type="warn">
          Small models (e.g. GPT-4o-mini) follow a "when in doubt → false" rule for privacy detection. If accuracy matters, configure a more
          capable TD model (GPT-4o or equivalent) at a low rank number.
        </InfoBox>
      </Section>

      {/* Models */}
      <Section
        icon={<Database size={16} className="text-blue-600" />}
        title="Models"
        color="bg-blue-100"
      >
        <p className="text-sm text-gray-600 mb-3">
          Under <Link to="/models" className="text-indigo-600 hover:underline">Models</Link> you manage the AI models available to your API keys.
          Each model has several flags that control when it's selected:
        </p>
        <div className="space-y-2 text-sm mb-3">
          {[
            { flag: 'ranked_position', detail: 'Lower number = higher priority. Unranked defaults to 999. This is the primary sort key for model selection.' },
            { flag: 'isPrivacyCompliant', detail: 'When a query is detected as requiring privacy, only models with this flag are eligible.' },
            { flag: 'taskDeterminationModel', detail: 'Marks the model as a TD model. The system picks the highest-ranked TD + privacy-compliant model for analysis.' },
            { flag: 'customTagSupport', detail: 'A per-tag boolean map (e.g. { marketing: true }). When a custom tag fires and filtering is enabled, only models with that tag set to true are used.' },
          ].map(({ flag, detail }) => (
            <div key={flag} className="flex gap-3 bg-gray-50 rounded-md px-3 py-2 border border-gray-100">
              <code className="text-indigo-600 font-mono text-xs flex-shrink-0 mt-0.5">{flag}</code>
              <p className="text-gray-600 text-xs">{detail}</p>
            </div>
          ))}
        </div>
        <InfoBox type="info">
          Model selection always respects the API key's <strong>selection mode</strong> (all / custom_only / global_only / privacy_only) and
          <strong> priority</strong> (rank vs price) before applying any further filtering.
        </InfoBox>
      </Section>

      {/* Model Lists */}
      <Section
        icon={<List size={16} className="text-teal-600" />}
        title="Model Lists"
        color="bg-teal-100"
      >
        <p className="text-sm text-gray-600 mb-3">
          A <strong>Model List</strong> is a static, curated set of models you create under the <em>Lists</em> tab on the{' '}
          <Link to="/models" className="text-indigo-600 hover:underline">Models</Link> page.
          Assigning a list to an API key restricts that key to <em>only</em> using models in the list.
        </p>
        <div className="text-sm text-gray-600 space-y-2 mb-3">
          <p>The key's <strong>selection mode</strong> and <strong>priority</strong> still apply <em>within</em> the list — so if the list has 5 models and the mode is <Tag>privacy_only</Tag>, only privacy-compliant models from that 5 are candidates.</p>
          <p>This is useful for white-labelling or cost control: give a client key a list with only approved, cost-effective models.</p>
        </div>
        <InfoBox type="warn">
          If no models in the list match the active filters (mode + privacy requirement), the request fails. Always ensure your list has at least one eligible model for each scenario you expect.
        </InfoBox>
      </Section>

      {/* Category Rules / Custom Tags */}
      <Section
        icon={<Shield size={16} className="text-green-600" />}
        title="Category Rules & Custom Tags"
        color="bg-green-100"
      >
        <p className="text-sm text-gray-600 mb-3">
          Under <Link to="/category-rules" className="text-indigo-600 hover:underline">Category Rules</Link> you define boolean detection tags
          that TD evaluates on every query. There are two kinds:
        </p>
        <div className="grid grid-cols-2 gap-3 text-xs mb-4">
          <div className="bg-gray-50 rounded-md px-3 py-3 border border-gray-100">
            <div className="flex items-center gap-1 mb-1">
              <Tag color="bg-green-100 text-green-700">Privacy</Tag>
              <span className="text-gray-400 font-normal ml-1">built-in</span>
            </div>
            <p className="text-gray-600">Always active. When <code>requiresPrivacy: true</code> is detected, the model pool is restricted to privacy-compliant models. Cannot be disabled.</p>
          </div>
          <div className="bg-gray-50 rounded-md px-3 py-3 border border-gray-100">
            <div className="flex items-center gap-1 mb-1">
              <Tag color="bg-blue-100 text-blue-700">Custom Tags</Tag>
              <span className="text-gray-400 font-normal ml-1">e.g. marketing, legal</span>
            </div>
            <p className="text-gray-600">You define the rules. TD always detects them, but model pool filtering only activates on keys where the tag slug is enabled in <em>Tag-Based Filtering</em>.</p>
          </div>
        </div>
        <p className="text-sm text-gray-600 mb-1">
          Each category has a <strong>Configure Rules</strong> tab (write the detection rules in plain text — what counts as true vs false) and a <strong>Test Center</strong> tab to verify detection with sample queries.
        </p>
        <InfoBox type="tip">
          Custom tags and Model Lists are complementary. A list gives a static model pool per key; a custom tag dynamically narrows that pool based on what's in the query. Use both together for fine-grained control.
        </InfoBox>
      </Section>

      {/* API Key Settings */}
      <Section
        icon={<Key size={16} className="text-indigo-600" />}
        title="API Key Settings"
        color="bg-indigo-100"
      >
        <p className="text-sm text-gray-600 mb-3">
          Each API key has five configurable settings that interact with the above systems:
        </p>
        <div className="space-y-3 text-sm mb-3">
          {[
            {
              setting: 'Force Task Determination',
              values: ['on (default)', 'off'],
              detail: 'When on, TD always runs and the system picks the best model — any model passed in the request body is ignored. When off, a caller-specified model is used directly (no TD cost). If no model is specified and TD is off, TD still runs automatically to select the best model.',
            },
            {
              setting: 'Model Selection Mode',
              values: ['all', 'custom_only', 'global_only', 'privacy_only'],
              detail: 'The first coarse filter — determines which model pool is eligible before any ranking or tag filtering.',
            },
            {
              setting: 'Model Selection Priority',
              values: ['rank', 'price'],
              detail: 'Within the eligible pool, rank sorts by ranked_position (quality); price sorts by cost per token (cheapest first).',
            },
            {
              setting: 'Model List',
              values: ['optional'],
              detail: 'Pins the key to a curated list. Selection mode and priority still apply within the list.',
            },
            {
              setting: 'Tag-Based Filtering',
              values: ['per-slug checkboxes'],
              detail: 'Opt-in enforcement gate for custom tags. TD always detects tags, but only slugs ticked here actually filter the model pool for this key.',
            },
          ].map(({ setting, values, detail }) => (
            <div key={setting} className="bg-gray-50 rounded-md px-3 py-3 border border-gray-100">
              <div className="flex flex-wrap items-center gap-1.5 mb-1">
                <span className="font-medium text-gray-800 text-xs">{setting}</span>
                {values.map(v => <Tag key={v} color="bg-gray-200 text-gray-600">{v}</Tag>)}
              </div>
              <p className="text-gray-600 text-xs">{detail}</p>
            </div>
          ))}
        </div>
        <InfoBox type="info">
          Tag-Based Filtering is API key requests only. JWT (browser) users get <code>customTags</code> in the task analysis result but no model pool restriction — that's by design.
        </InfoBox>
      </Section>

      {/* Quick reference */}
      <Section
        icon={<Settings size={16} className="text-gray-600" />}
        title="Quick Setup Reference"
        color="bg-gray-100"
      >
        <div className="space-y-3 text-sm text-gray-700">
          {[
            {
              goal: 'Route privacy-sensitive queries to compliant models only',
              steps: ['Mark models as Privacy Compliant in Models', 'Privacy detection is always on — no key config needed'],
            },
            {
              goal: 'Use a specific subset of models for a key',
              steps: ['Create a Model List with the desired models', 'Assign the list to the key in API Key Settings'],
            },
            {
              goal: 'Route marketing queries to marketing-capable models',
              steps: ['Create a "Marketing" category in Category Rules', 'Write detection rules and test them', 'Tick "Marketing" in each capable model\'s Custom Tags', 'Enable "Marketing" in the key\'s Tag-Based Filtering'],
            },
            {
              goal: 'Let callers choose their own model (bypass TD)',
              steps: ['Uncheck Force Task Determination in the key\'s settings', 'Caller passes a model in the request — it is used directly, no TD cost (quota not consumed)', 'If caller sends no model and TD is off, TD still runs to pick the best model — full TD cost applies'],
            },
            {
              goal: 'Use your own AI backend for task determination',
              steps: ['Add a custom endpoint model in Models', 'Tick Task Determination Model + Privacy Compliant', 'Set ranked_position to a low number (e.g. 10) so it wins'],
            },
          ].map(({ goal, steps }) => (
            <div key={goal} className="bg-gray-50 rounded-md px-3 py-3 border border-gray-100">
              <p className="font-medium text-gray-800 text-xs mb-2">{goal}</p>
              <ol className="space-y-1 list-decimal list-inside">
                {steps.map(s => <li key={s} className="text-xs text-gray-600">{s}</li>)}
              </ol>
            </div>
          ))}
        </div>
      </Section>
    </div>
  );
}
